import { plural, t } from "@lingui/core/macro";
import { makeAutoObservable, observable } from "mobx";

import type {
  AdverseEventLevel,
  AdverseEventType,
  DeathEventReason,
  EventCategory,
  HospitalizationEventReason,
  HospitalizationEventType,
  LabsEventType,
  PhysicianNotifiedEventType,
  VisitEventPlannedCategory,
  VisitEventType,
  VisitEventUnplannedCategory,
} from "../../../api";
import type { DialogService } from "../../../components/ui-kit/dialog-service";

import { hasRichText } from "../../../components/ui-kit/sanitize-rich-text";
import { getServerTodayISO } from "../../../utils/server-time";
import { createRowId } from "../../../utils/utils";

/**
 * Draft rows of the add-events dialog, held by the patient rather than by the
 * dialog or the Events tab, so neither hiding nor switching tabs throws away
 * what was typed. Replaces
 * [`useRowAccordion`](../../../components/ui-kit/tables/row-accordion.tsx) for
 * this one dialog; `rows` is reassigned rather than mutated in place, so a
 * component that only reads `draft.rows` still re-renders.
 */
export class EventsDraft {
  rows: EventRow[] = [];
  expandedRowId = "";
  /**
   * Whether the dialog is on screen. Here rather than in the button's state so
   * the leave guard can put the rows back on screen when the user keeps editing.
   */
  dialogOpen = false;
  /**
   * How far the dialog content was scrolled, so re-opening resumes there. Boxed
   * so `reset` can hand out a new box: the dialog unmounts *after* the reset and
   * writes one last reading on its way out, which would undo the zeroing here.
   */
  scroll = { top: 0 };

  constructor() {
    // `scroll` is opted out: nothing renders it, and the dialog's scroll
    // listener would otherwise write to an observable on every scroll event.
    makeAutoObservable(this, { scroll: false });
    this.reset();
  }

  addRow() {
    const row = createEventRow();
    this.rows = [...this.rows, row];
    this.expandedRowId = row.__rowId;
  }

  removeRow(rowId: string) {
    const index = this.rows.findIndex((r) => r.__rowId === rowId);
    if (index === -1) {
      return;
    }
    const rows = this.rows.filter((r) => r.__rowId !== rowId);
    if (this.expandedRowId === rowId) {
      // Expand what took its place, or the row above if it was the last one.
      this.expandedRowId =
        rows[index]?.__rowId ?? rows[index - 1]?.__rowId ?? "";
    }
    this.rows = rows;
  }

  toggleRow(rowId: string) {
    this.expandedRowId = this.expandedRowId === rowId ? "" : rowId;
  }

  /** Back to a single blank row — call once a draft is saved or dropped. */
  reset() {
    // Also closed: dropping the rows from the leave guard leaves the dialog
    // flagged open, and it would spring back up on the next visit.
    this.dialogOpen = false;
    this.scroll = { top: 0 };
    const row = createEventRow();
    this.rows = [row];
    this.expandedRowId = row.__rowId;
  }

  /** Rows the user has actually started, for the "Add events" badge. */
  startedRowCount() {
    return this.rows.filter(isEventRowStarted).length;
  }
}

/**
 * Asked both by the dialog's Cancel and by the guard that fires when the user
 * leaves the patient — shared so the two prompts can't drift into two wordings
 * (and two message ids). Resolves true when the rows should be dropped.
 */
export function confirmDiscardEvents(
  confirm: DialogService["confirm"],
  started: number,
) {
  return confirm(
    plural(started, {
      one: "It won't be added to the patient's record.",
      other: "They won't be added to the patient's record.",
    }),
    plural(started, {
      one: "Discard # unsaved event?",
      other: "Discard # unsaved events?",
    }),
    { confirmLabel: t`Discard`, cancelLabel: t`Keep editing` },
  );
}

export type EventRow = {
  __rowId: string;
  /** The pre-filled date this row was created with — see isEventRowStarted. */
  __initialDate: string;
  event_date: string;
  event_category: EventCategory | null;
  event_category_type: EventCategoryTypeValue | null;
  event_category_details: EventCategoryDetailsValue | null;
  event_description: string | null;
  event_comment: string | null;
};

type EventCategoryTypeValue =
  | HospitalizationEventType
  | DeathEventReason
  | VisitEventType
  | LabsEventType
  | PhysicianNotifiedEventType
  | AdverseEventType;

type EventCategoryDetailsValue =
  | HospitalizationEventReason
  | VisitEventUnplannedCategory
  | VisitEventPlannedCategory
  | AdverseEventLevel;

function createEventRow(): EventRow {
  const today = getServerTodayISO();
  return observable({
    __rowId: createRowId(),
    __initialDate: today,
    event_date: today,
    event_category: null,
    event_category_type: null,
    event_category_details: null,
    event_description: null,
    event_comment: null,
  });
}

/**
 * Whether anything has been entered into a row, so an untouched blank row leaves
 * the "Add events" button unbadged. The date is pre-filled, so it counts only
 * once changed — compared against the row's own initial value rather than today,
 * so a dialog left open across midnight doesn't turn untouched rows "started".
 */
function isEventRowStarted(row: EventRow): boolean {
  return Boolean(
    row.event_category ||
    row.event_category_type ||
    row.event_category_details ||
    hasRichText(row.event_description) ||
    hasRichText(row.event_comment) ||
    row.event_date !== row.__initialDate,
  );
}
